
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { WorldCountry } from '@/data/countries/types';

interface CountrySEOContentProps {
  country: WorldCountry;
}

const CountrySEOContent: React.FC<CountrySEOContentProps> = ({ country }) => {
  return (
    <div className="bg-white py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Main SEO Content Section */}
          <section className="mb-12">
          <h2 className="text-3xl font-bold text-brand-gray-900 mb-6">
            Find the Best Therapists, Psychologists & Psychotherapists in {country.name}
          </h2>
          <div className="prose prose-lg max-w-none text-brand-gray-700">
            <p className="mb-6">
              Discovering the <strong>best therapists, psychologists, and psychotherapists in {country.name}</strong> has never been easier. 
              Our comprehensive directory connects you with highly qualified mental health professionals who 
              have completed specialized training under the supervision of renowned expert Dr. José Luis Marín. 
              When searching for the <strong>best mental health professionals in {country.name}</strong>, it's essential 
              to find licensed therapists, experienced psychologists, and certified psychotherapists who combine clinical expertise with cultural understanding.
            </p>
            
            <p className="mb-6">
              The <strong>best therapists, psychologists, and psychotherapists in {country.name}</strong> featured on our platform are 
              certified professionals who have undergone rigorous training in Brief Psychotherapy techniques. 
              These mental health specialists - including licensed therapists, certified psychologists, and professional psychotherapists - understand the unique psychological challenges faced by residents 
              of {country.name} and provide culturally sensitive therapeutic interventions tailored to local needs.
            </p>
            
            <p className="mb-6">
              Our network of the <strong>best therapists, psychologists, and psychotherapists in {country.name}</strong> offers both 
              in-person and online therapy sessions, ensuring accessibility for all residents regardless 
              of their location within the country. Whether you're dealing with anxiety, depression, 
              relationship issues, or trauma, the experienced <strong>therapists, psychologists, and psychotherapists in {country.name}</strong> 
              are equipped with evidence-based treatment modalities to support your mental health journey.
            </p>
          </div>
          </section>

          {/* Why Choose Section */}
          <section className="mb-12 bg-brand-blue-50 p-8 rounded-xl">
            <h3 className="text-2xl font-semibold text-brand-gray-900 mb-6">
              Why Choose the Best Therapists, Psychologists & Psychotherapists in {country.name}?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-lg font-medium text-brand-gray-900 mb-3">Verified Credentials</h4>
                <p className="text-brand-gray-700">
                  All the <strong>best therapists, psychologists, and psychotherapists in {country.name}</strong> on our platform have 
                  completed the Master's Degree in Brief Psychotherapy Training, ensuring you receive 
                  treatment from qualified mental health professionals.
                </p>
              </div>
              <div>
                <h4 className="text-lg font-medium text-brand-gray-900 mb-3">Cultural Competency</h4>
                <p className="text-brand-gray-700">
                  The <strong>licensed therapists, certified psychologists, and professional psychotherapists in {country.name}</strong> understand local cultural 
                  contexts and provide therapy that respects your background and values.
                </p>
              </div>
              <div>
                <h4 className="text-lg font-medium text-brand-gray-900 mb-3">Flexible Scheduling</h4>
                <p className="text-brand-gray-700">
                  Connect with the <strong>best therapists, psychologists, and psychotherapists in {country.name}</strong> for sessions 
                  that fit your schedule, including evening and weekend appointments.
                </p>
              </div>
              <div>
                <h4 className="text-lg font-medium text-brand-gray-900 mb-3">Immediate Availability</h4>
                <p className="text-brand-gray-700">
                  Access the <strong>experienced therapists, qualified psychologists, and expert psychotherapists in {country.name}</strong> for urgent 
                  consultations through our "Therapy Now" feature for immediate mental health support.
                </p>
              </div>
            </div>
          </section>

          {/* Specializations Section */}
          <section className="mb-12">
            <h3 className="text-2xl font-semibold text-brand-gray-900 mb-6">
              Specializations of the Best Therapists, Psychologists & Psychotherapists in {country.name}
            </h3>
            <div className="prose prose-lg max-w-none text-brand-gray-700">
              <p className="mb-4">
                The <strong>best therapists, psychologists, and psychotherapists in {country.name}</strong> offer a wide range of 
                therapeutic specializations to address diverse mental health needs:
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 list-disc list-inside mb-6">
                <li>Anxiety and Panic Disorders</li>
                <li>Depression and Mood Disorders</li>
                <li>Trauma and PTSD Treatment</li>
                <li>Relationship and Couples Therapy</li>
                <li>Family Counseling</li>
                <li>Addiction Recovery</li>
                <li>Stress Management</li>
                <li>Grief and Loss Counseling</li>
              </ul>
              <p>
                When you choose the <strong>experienced therapists, certified psychologists, and professional psychotherapists in {country.name}</strong> through 
                our platform, you're connecting with mental health professionals who stay current with the latest 
                therapeutic techniques and maintain the highest standards of clinical practice.
              </p>
            </div>
          </section>

          {/* Getting Started Section */}
          <section className="bg-brand-gray-50 p-8 rounded-xl">
            <h3 className="text-2xl font-semibold text-brand-gray-900 mb-4">
              Start Your Journey with the Best Therapists, Psychologists & Psychotherapists in {country.name}
            </h3>
            <p className="text-lg text-brand-gray-700 mb-6">
              Taking the first step toward better mental health is easier when you have access to the 
              <strong> best therapists, psychologists, and psychotherapists in {country.name}</strong>. Our platform streamlines the 
              process of finding and connecting with qualified mental health professionals in your area.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/find">
                <Button className="bg-brand-blue-600 text-white px-6 py-3 rounded-lg hover:bg-brand-blue-700 transition-colors">
                  Find Therapists & Psychologists in {country.name}
                </Button>
              </Link>
              <Link to="/auth">
                <Button variant="outline" className="bg-white text-brand-blue-600 border border-brand-blue-600 px-6 py-3 rounded-lg hover:bg-brand-blue-50 transition-colors">
                  Schedule Consultation Online
                </Button>
              </Link>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default CountrySEOContent;
